/**
 * useNextBestStep Hook
 * 
 * Determines the most relevant next action for the user based on their current state.
 * Returns a single recommendation at a time, following priority order.
 */

import { useState, useEffect, useCallback } from 'react';

// Hidden feature discovery pool — rotates randomly in CoachStrip secondary CTA
const DISCOVERY_FEATURES = [
  { label: 'Explore MIQ', action: 'VIEW_MIQ', icon: '📊' },
  { label: 'Workout Planner', action: 'VIEW_MIQ', icon: '💪' },
  { label: 'Peptide Planner', action: 'VIEW_PEPTIDE', icon: '💉' },
  { label: 'IF Timer', action: 'VIEW_IF_TIMER', icon: '⏱️' },
];

const pickRandomFeature = () => DISCOVERY_FEATURES[Math.floor(Math.random() * DISCOVERY_FEATURES.length)];

// Prompt IDs for dismissal tracking
const PROMPT_IDS = {
  GOALS_SET_CONFIRMATION: 'goals_set_confirmation',  // NEW: Post-goals confirmation
  SET_FIRST_GOAL: 'set_first_goal',
  SET_TIMELINE: 'set_timeline',
  SET_MEAL_PATTERN: 'set_meal_pattern',
  COMPLETE_PROFILE: 'complete_profile',
  POST_PROFILE_LOG_MEAL: 'post_profile_log_meal',  // RULE A: After profile, < 3 meals
  MEAL_PLANNER_NUDGE: 'meal_planner_nudge',        // RULE B: 3+ meals, planner unused
  LUNCH_DECISION_NUDGE: 'lunch_decision_nudge',    // RULE C: Lunch time decision support
  RESTAURANT_MENTION: 'restaurant_mention',         // RULE D: Restaurant keyword detected
  MEAL_PLANNER_EXIT: 'smartmeals_exit',          // RULE E: Meal planner exit recovery
  EVENING_SNACK_ASSIST: 'evening_snack_assist',    // RULE I: Evening snack assist
  MISSED_MEAL_RECOVERY: 'missed_meal_recovery',    // RULE J: Missed meal recovery
  // POST-MEAL COMMITMENT FLOW (Rules K, L, M)
  POST_MEAL_PROFILE_COMMIT: 'post_meal_profile_commit',     // RULE K: First meal → Complete Profile
  GOALS_TIMELINE_HOOK: 'goals_timeline_hook',               // RULE L: Profile done → Set Goals & Timeline
  JOURNEY_STARTED: 'journey_started',                       // RULE M: Journey confirmation (one-time)
  LOG_ANOTHER_MEAL: 'log_another_meal',
  VIEW_MIQ: 'view_miq',
  DAILY_CHECKIN: 'daily_checkin',
  TRY_PREMIUM_FEATURE: 'try_premium_feature'
};

// Dismiss durations in milliseconds
const DISMISS_DURATION_24H = 24 * 60 * 60 * 1000;  // 24 hours
const DISMISS_DURATION_48H = 48 * 60 * 60 * 1000;  // 48 hours
const DISMISS_DURATION_SESSION = 'session';        // Session-based (cleared on page refresh)
const DISMISS_DURATION_SAME_DAY = 'same_day';      // Until midnight local time

// Default dismiss duration
const DISMISS_DURATION = DISMISS_DURATION_24H;

// Custom durations for specific prompts
const PROMPT_DISMISS_DURATIONS = {
  [PROMPT_IDS.MEAL_PLANNER_NUDGE]: DISMISS_DURATION_48H,  // RULE B: 48 hours
  [PROMPT_IDS.RESTAURANT_MENTION]: DISMISS_DURATION_SESSION,  // RULE D: Session-based
  [PROMPT_IDS.EVENING_SNACK_ASSIST]: DISMISS_DURATION_SAME_DAY,  // RULE I: Same-day
  [PROMPT_IDS.MISSED_MEAL_RECOVERY]: DISMISS_DURATION_SAME_DAY,  // RULE J: Same-day
  [PROMPT_IDS.POST_MEAL_PROFILE_COMMIT]: DISMISS_DURATION_24H,  // RULE K: 24 hours
  [PROMPT_IDS.GOALS_TIMELINE_HOOK]: DISMISS_DURATION_48H,  // RULE L: 48 hours
};

// Session-based dismiss tracking (cleared on page refresh)
const sessionDismissed = new Set();

/**
 * Get today's date string for same-day dismissal tracking
 */
const getTodayDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Check if a prompt was dismissed within its dismiss duration
 */
const isPromptDismissed = (userId, promptId) => {
  if (!userId) return false;
  
  const duration = PROMPT_DISMISS_DURATIONS[promptId];
  
  // Check session-based dismissal
  if (duration === DISMISS_DURATION_SESSION) {
    return sessionDismissed.has(`${userId}_${promptId}`);
  }
  
  // Check same-day dismissal
  if (duration === DISMISS_DURATION_SAME_DAY) {
    const key = `coach_dismissed_${userId}_${promptId}_date`;
    const dismissedDate = localStorage.getItem(key);
    return dismissedDate === getTodayDateString();
  }
  
  // Standard time-based dismissal
  const key = `coach_dismissed_${userId}_${promptId}`;
  const dismissedAt = localStorage.getItem(key);
  if (!dismissedAt) return false;
  
  const dismissedTime = parseInt(dismissedAt, 10);
  const effectiveDuration = duration || DISMISS_DURATION;
  return Date.now() - dismissedTime < effectiveDuration;
};

/**
 * Dismiss a prompt (handles session-based, same-day, and localStorage-based)
 */
const dismissPrompt = (userId, promptId) => {
  if (!userId) return;
  
  const duration = PROMPT_DISMISS_DURATIONS[promptId];
  
  if (duration === DISMISS_DURATION_SESSION) {
    sessionDismissed.add(`${userId}_${promptId}`);
  } else if (duration === DISMISS_DURATION_SAME_DAY) {
    const key = `coach_dismissed_${userId}_${promptId}_date`;
    localStorage.setItem(key, getTodayDateString());
  } else {
    const key = `coach_dismissed_${userId}_${promptId}`;
    localStorage.setItem(key, Date.now().toString());
  }
};

/**
 * Check if MIQ tab has ever been visited
 */
const hasVisitedMIQTab = (userId) => {
  if (!userId) return false;
  return localStorage.getItem(`miq_tab_visited_${userId}`) === 'true';
};

/**
 * Mark MIQ tab as visited
 */
const markMIQTabVisited = (userId) => {
  if (!userId) localStorage.setItem(`miq_tab_visited_${userId}`, 'true');
};

/**
 * Check if "journey started" toast has been shown (one-time only)
 */
const hasShownJourneyStarted = (userId) => {
  if (!userId) return true; // Default to shown if no user
  return localStorage.getItem(`journey_started_shown_${userId}`) === 'true';
};

/**
 * Mark "journey started" toast as shown (one-time)
 */
const markJourneyStartedShown = (userId) => {
  if (userId) localStorage.setItem(`journey_started_shown_${userId}`, 'true');
};

/**
 * Check if barcode scanner has been used
 */
const hasUsedBarcodeScanner = (userId) => {
  if (!userId) return false;
  return localStorage.getItem(`barcode_used_${userId}`) === 'true';
};

/**
 * Check if meal planner has been used
 */
const hasUsedMealPlanner = (userId) => {
  if (!userId) return false;
  return localStorage.getItem(`meal_planner_used_${userId}`) === 'true';
};

/**
 * Mark meal planner as used
 */
const markMealPlannerUsed = (userId) => {
  if (userId) localStorage.setItem(`meal_planner_used_${userId}`, 'true');
};

/**
 * Track meal planner exit without generating (for RULE E)
 */
const markMealPlannerExitWithoutGenerate = (userId) => {
  if (userId) localStorage.setItem(`meal_planner_exited_${userId}`, Date.now().toString());
};

/**
 * Check if user exited meal planner without generating (within last 24h)
 */
const hasExitedMealPlannerWithoutGenerate = (userId) => {
  if (!userId) return false;
  const exitedAt = localStorage.getItem(`meal_planner_exited_${userId}`);
  if (!exitedAt) return false;
  
  const exitTime = parseInt(exitedAt, 10);
  // Only show for 24 hours after exit
  return Date.now() - exitTime < DISMISS_DURATION_24H;
};

/**
 * Clear meal planner exit flag (called when plan is generated)
 */
const clearMealPlannerExitFlag = (userId) => {
  if (userId) localStorage.removeItem(`meal_planner_exited_${userId}`);
};

/**
 * Common restaurant keywords for detection (RULE D)
 */
const RESTAURANT_KEYWORDS = [
  // Fast food
  "mcdonald", "burger king", "wendy", "subway", "taco bell", "kfc", "chick-fil-a",
  "chipotle", "five guys", "in-n-out", "popeye", "arby", "sonic", "jack in the box",
  "carl's jr", "hardee", "whataburger", "shake shack", "panera", "panda express",
  // Casual dining
  "applebee", "chili", "olive garden", "red lobster", "outback", "longhorn",
  "texas roadhouse", "cracker barrel", "ihop", "denny", "waffle house", "cheesecake factory",
  "buffalo wild wings", "hooters", "tgi friday", "ruby tuesday", "red robin",
  // Coffee/Fast casual
  "starbucks", "dunkin", "tim horton", "peet", "caribou coffee", "dutch bros",
  "sweetgreen", "cava", "noodles", "mod pizza", "blaze pizza", "jersey mike",
  // Generic indicators
  "restaurant", "diner", "cafe", "grill", "bistro", "kitchen", "eatery", "pizzeria",
  "takeout", "takeaway", "delivery", "uber eats", "doordash", "grubhub", "eating out",
  "fast food", "drive-thru", "drive through"
];

/**
 * Check if text contains restaurant keywords (RULE D)
 */
const containsRestaurantKeyword = (text) => {
  if (!text) return false;
  const lowerText = text.toLowerCase();
  return RESTAURANT_KEYWORDS.some(keyword => lowerText.includes(keyword));
};

/**
 * Check if current time is within lunch window (11:30am - 1:30pm)
 * DEV OVERRIDE: localStorage 'TEST_MODE_COACH_TIME' bypasses time check for verification
 */
const isLunchTime = () => {
  // DEV override for testing time-gated rules
  if (typeof window !== 'undefined' && localStorage.getItem('TEST_MODE_COACH_TIME') === 'true') {
    return true;
  }
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const timeInMinutes = hours * 60 + minutes;
  
  // 11:30am = 690 minutes, 1:30pm = 810 minutes
  return timeInMinutes >= 690 && timeInMinutes <= 810;
};

/**
 * Check if current time is evening (6pm - 9pm) - for RULE I
 * DEV OVERRIDE: localStorage 'TEST_MODE_COACH_TIME' bypasses time check for verification
 */
const isEveningTime = () => {
  // DEV override for testing time-gated rules
  if (typeof window !== 'undefined' && localStorage.getItem('TEST_MODE_COACH_TIME') === 'true') {
    return true;
  }
  const now = new Date();
  const hours = now.getHours();
  // 6pm (18:00) to 9pm (21:00)
  return hours >= 18 && hours < 21;
};

/**
 * Check if it's late in the day and user may have missed meals (after 2pm) - for RULE J
 * DEV OVERRIDE: localStorage 'TEST_MODE_COACH_TIME' bypasses time check for verification
 */
const isAfternoon = () => {
  // DEV override for testing time-gated rules
  if (typeof window !== 'undefined' && localStorage.getItem('TEST_MODE_COACH_TIME') === 'true') {
    return true;
  }
  const now = new Date();
  const hours = now.getHours();
  // After 2pm (14:00)
  return hours >= 14;
};

/**
 * Static real-food suggestions for low calorie situations
 * Rotating list - pick randomly to keep it fresh
 */
const EVENING_SNACK_SUGGESTIONS = [
  { name: "Greek yogurt with berries", calories: "~150", benefit: "protein + antioxidants" },
  { name: "Apple slices with almond butter", calories: "~200", benefit: "fiber + healthy fats" },
  { name: "Handful of mixed nuts", calories: "~170", benefit: "satisfying + heart-healthy" },
  { name: "Cottage cheese with fruit", calories: "~180", benefit: "high protein + calcium" },
  { name: "Hard-boiled eggs (2)", calories: "~140", benefit: "complete protein" },
  { name: "Hummus with veggie sticks", calories: "~150", benefit: "fiber + plant protein" },
  { name: "String cheese + crackers", calories: "~160", benefit: "calcium + carbs" },
  { name: "Trail mix (small handful)", calories: "~180", benefit: "energy + nutrients" },
];

// 30 Quick Meal Ideas - Rotates to show fresh suggestions
const MISSED_MEAL_SUGGESTIONS = [
  // Quick Sandwiches & Wraps
  { name: "Quick turkey sandwich", calories: "~350", benefit: "balanced macros" },
  { name: "Chicken wrap", calories: "~400", benefit: "filling + protein-rich" },
  { name: "Tuna salad with crackers", calories: "~280", benefit: "lean protein + omega-3" },
  { name: "BLT on whole wheat", calories: "~380", benefit: "satisfying + quick" },
  { name: "Grilled cheese + tomato soup", calories: "~450", benefit: "comfort food classic" },
  { name: "Veggie hummus wrap", calories: "~320", benefit: "fiber + plant protein" },
  
  // Protein-Focused
  { name: "Rotisserie chicken + veggies", calories: "~350", benefit: "high protein + low effort" },
  { name: "Protein smoothie", calories: "~300", benefit: "fast + nutritious" },
  { name: "Hard-boiled eggs + avocado toast", calories: "~380", benefit: "protein + healthy fats" },
  { name: "Canned salmon on salad", calories: "~320", benefit: "omega-3 + protein" },
  { name: "Deli meat roll-ups with cheese", calories: "~250", benefit: "low-carb + protein" },
  
  // Quick Cook (10 min or less)
  { name: "Scrambled eggs with toast", calories: "~350", benefit: "quick breakfast anytime" },
  { name: "Bean and cheese quesadilla", calories: "~350", benefit: "protein + fiber" },
  { name: "Instant ramen + egg", calories: "~400", benefit: "fast + satisfying" },
  { name: "Microwave sweet potato + Greek yogurt", calories: "~280", benefit: "fiber + protein" },
  { name: "Cheese omelette", calories: "~300", benefit: "high protein + quick" },
  
  // No-Cook Options
  { name: "Peanut butter banana toast", calories: "~320", benefit: "quick energy + protein" },
  { name: "Overnight oats (prepared)", calories: "~300", benefit: "fiber + sustained energy" },
  { name: "Cottage cheese + fruit bowl", calories: "~250", benefit: "protein + vitamins" },
  { name: "Greek yogurt parfait", calories: "~280", benefit: "protein + probiotics" },
  { name: "Cheese & crackers + fruit", calories: "~300", benefit: "balanced snack meal" },
  
  // Hearty Meals
  { name: "Leftover stir-fry reheated", calories: "~400", benefit: "balanced + easy" },
  { name: "Frozen burrito + salsa", calories: "~380", benefit: "convenient + filling" },
  { name: "Soup + bread", calories: "~350", benefit: "warm + satisfying" },
  { name: "Rice bowl with canned beans", calories: "~420", benefit: "fiber + carbs" },
  { name: "Pasta with jarred sauce + parmesan", calories: "~450", benefit: "quick comfort food" },
  
  // Lighter Options
  { name: "Caprese salad", calories: "~280", benefit: "fresh + light" },
  { name: "Smoothie bowl", calories: "~350", benefit: "refreshing + nutritious" },
  { name: "Avocado toast + cherry tomatoes", calories: "~300", benefit: "healthy fats + fiber" },
  { name: "Veggie & bean soup", calories: "~250", benefit: "low-cal + filling" },
];

/**
 * Get a random suggestion from the list
 */
const getRandomSuggestion = (suggestions) => {
  return suggestions[Math.floor(Math.random() * suggestions.length)];
};

/**
 * Main hook
 */
const useNextBestStep = ({
  userId,
  userProfile,
  totalMealsLogged,      // Today's meals count
  allTimeMealsLogged,    // All-time meals count (from MIQ confidence)
  hasGoal,
  hasTimeline,
  hasMealPattern,
  aiNutritionGoals,  // NEW: For goals confirmation prompt
  lastMealTimestamp,     // NEW: For RULE C - time since last meal
  detectedRestaurantText, // NEW: For RULE D - restaurant keyword detection
  mealPlannerJustExited, // NEW: For RULE E - meal planner exit recovery
  todayCalories,         // NEW: For RULE I/J - today's calories consumed
  advancedGoals,         // NEW: For RULE I/J - fallback goal source
  nutritionGoals,        // NEW: For RULE I/J - fallback goal source
  lastDailyCheckin,
  checkedInToday,
  subscriptionDetails,
  currentView,
  showOnboarding,
  isTestMode = false
}) => {
  const [currentPrompt, setCurrentPrompt] = useState(null);
  
  // Use all-time meals if available, otherwise fall back to today's
  const effectiveMealsLogged = allTimeMealsLogged ?? totalMealsLogged;
  
  // Calculate daily goal and remaining calories internally
  const dailyCalorieGoal = aiNutritionGoals?.nutrition_goals?.daily_targets?.calories 
    || advancedGoals?.daily_calorie_target 
    || userProfile?.daily_calories 
    || 2000;
  const caloriesRemaining = dailyCalorieGoal - (todayCalories || 0);
  
  // Determine user tier
  const isPremiumOrTrial = subscriptionDetails?.is_premium || 
                           subscriptionDetails?.is_trial || 
                           subscriptionDetails?.is_trialing;
  
  const isMIQTier = isPremiumOrTrial || 
                   subscriptionDetails?.plan_key === 'miq_trial' ||
                   subscriptionDetails?.plan_key === 'miq_monthly';
  
  // Check if user is in a modal or onboarding
  const isInModal = showOnboarding;
  
  // Evaluate prompts in priority order
  useEffect(() => {
    // Don't show prompts if in modal or no user
    if (isInModal || !userId) {
      setCurrentPrompt(null);
      return;
    }
    
    const onboardingCompleted = userProfile?.onboarding_completed === true;
    const hasMIQBeenVisited = hasVisitedMIQTab(userId);
    
    // Log for debugging in test mode
    const log = (msg) => {
      if (isTestMode) console.log(`[COACH] ${msg}`);
    };
    
    // ═══════════════════════════════════════════════════════════════════════════
    // GOLDEN PATH: Single primary CTA at each stage
    // Priority order follows the linear journey, not intermediate steps
    // "Calories = entry point, Profile + Goals = commitment, MIQ = journey"
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Check if AI nutrition goals are set
    const aiNutritionGoalsSet = !!(aiNutritionGoals?.nutrition_goals?.daily_targets?.calories);
    
    // ═══════════════════════════════════════════════════════════════════════════
    // RULE K — Immediate Post-Meal Commitment Nudge (P0)
    // Trigger: totalMealsLogged === 1 && profile_completed === false
    // "Nice — MIQ captured its first signal."
    // ═══════════════════════════════════════════════════════════════════════════
    if (totalMealsLogged >= 1 && !onboardingCompleted) {
      if (!isPromptDismissed(userId, PROMPT_IDS.POST_MEAL_PROFILE_COMMIT)) {
        log('Showing: POST_MEAL_PROFILE_COMMIT (RULE K)');
        setCurrentPrompt({
          promptId: PROMPT_IDS.POST_MEAL_PROFILE_COMMIT,
          message: "Nice — MIQ captured its first signal.",
          subMessage: "Complete your profile so MIQ can personalize targets and timing for you.",
          primaryLabel: "Complete Profile",
          primaryAction: 'COMPLETE_PROFILE',
          secondaryCTA: pickRandomFeature(),
          icon: '🧠',
          variant: 'commitment'
        });
        return;
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // RULE L — SmartGoals Intelligence Hook (P0)
    // Trigger: profile_completed === true && ai_nutrition_goals_set === false
    // Premium intelligence-style prompt to activate SmartGoals
    // ═══════════════════════════════════════════════════════════════════════════
    if (onboardingCompleted && !aiNutritionGoalsSet) {
      if (!isPromptDismissed(userId, PROMPT_IDS.GOALS_TIMELINE_HOOK)) {
        log('Showing: GOALS_TIMELINE_HOOK (RULE L)');
        setCurrentPrompt({
          promptId: PROMPT_IDS.GOALS_TIMELINE_HOOK,
          message: "Fuel your goals with intelligence.",
          subMessage: "Set SmartGoals to activate personalized metabolic guidance powered by MIQ.",
          primaryLabel: "🎯 Activate SmartGoals",
          primaryAction: 'SET_GOALS_TIMELINE',
          icon: '🎯',
          variant: 'intelligence'
        });
        return;
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // RULE M — "Journey Started" Confirmation (P0)
    // Trigger: profile_completed && ai_nutrition_goals_set && totalMealsLogged >= 1
    // One-time toast (non-blocking, auto-dismiss)
    // ═══════════════════════════════════════════════════════════════════════════
    if (onboardingCompleted && aiNutritionGoalsSet && totalMealsLogged >= 1) {
      if (!hasShownJourneyStarted(userId)) {
        log('Showing: JOURNEY_STARTED (RULE M) - Toast');
        setCurrentPrompt({
          promptId: PROMPT_IDS.JOURNEY_STARTED,
          message: "✅ Your metabolic journey has started.",
          subMessage: "MIQ will now refine targets as patterns emerge from your meals.",
          isToast: true,  // Special flag for toast rendering
          autoDismiss: true,
          icon: '🚀',
          variant: 'success'
        });
        // Mark as shown immediately (one-time only)
        markJourneyStartedShown(userId);
        return;
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // RULE A: Post-Profile Log Meal Nudge (continues the journey after commitment)
    // Trigger: profile_completed === true && meals_logged < 3
    // ═══════════════════════════════════════════════════════════════════════════
    if (onboardingCompleted && aiNutritionGoalsSet && effectiveMealsLogged < 3) {
      if (!isPromptDismissed(userId, PROMPT_IDS.POST_PROFILE_LOG_MEAL)) {
        log('Showing: POST_PROFILE_LOG_MEAL (RULE A)');
        setCurrentPrompt({
          promptId: PROMPT_IDS.POST_PROFILE_LOG_MEAL,
          message: "MIQ is learning your patterns. Log 1–2 more meals to sharpen its accuracy.",
          primaryLabel: "Log a Meal",
          primaryAction: 'TAKE_PHOTO',
          secondaryCTA: pickRandomFeature(),
          icon: '🧠'
        });
        return;
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // RULE B: Meal Planner Activation Nudge - DISABLED
    // This is now handled by the permanent SmartActionsCard component
    // Keeping the code commented for reference
    // ═══════════════════════════════════════════════════════════════════════════
    // const mealPlannerUsed = hasUsedMealPlanner(userId);
    // if (onboardingCompleted && aiNutritionGoalsSet && effectiveMealsLogged >= 3 && !mealPlannerUsed) {
    //   ... removed - now permanent SmartActionsCard feature
    // }
    const mealPlannerUsed = hasUsedMealPlanner(userId);
    
    // ═══════════════════════════════════════════════════════════════════════════
    // EATING OUT — SMART PICKS (Rules C, D, E)
    // "Smart decisions when nutrition isn't exact."
    // MIQ helps users choose intelligently when menus aren't precise
    // Only show after commitment flow is complete (profile + goals)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // RULE C: Lunch Decision Nudge
    // Trigger: profile_completed + goals_set + current_time 11:30am-1:30pm + no meal in last 4 hours
    // Dismiss: 24 hours
    const fourHoursAgo = Date.now() - (4 * 60 * 60 * 1000);
    const hasRecentMeal = lastMealTimestamp && lastMealTimestamp > fourHoursAgo;
    
    if (onboardingCompleted && aiNutritionGoalsSet && isLunchTime() && !hasRecentMeal) {
      if (!isPromptDismissed(userId, PROMPT_IDS.LUNCH_DECISION_NUDGE)) {
        log('Showing: LUNCH_DECISION_NUDGE (RULE C)');
        setCurrentPrompt({
          promptId: PROMPT_IDS.LUNCH_DECISION_NUDGE,
          message: "Eating out today? MIQ can suggest Smart Picks that still fit your targets.",
          primaryLabel: "See Smart Picks",
          primaryAction: 'SMART_PICKS',
          icon: '🍽️',
          variant: 'miq'
        });
        return;
      }
    }
    
    // RULE D: Restaurant Mention Detection
    // Trigger: Manual entry text contains restaurant keyword or proper noun
    // Dismiss: Session-based
    if (onboardingCompleted && aiNutritionGoalsSet && detectedRestaurantText && containsRestaurantKeyword(detectedRestaurantText)) {
      if (!isPromptDismissed(userId, PROMPT_IDS.RESTAURANT_MENTION)) {
        log('Showing: RESTAURANT_MENTION (RULE D)');
        setCurrentPrompt({
          promptId: PROMPT_IDS.RESTAURANT_MENTION,
          message: "MIQ can help you choose Smart Picks here based on your goals.",
          primaryLabel: "See Smart Picks",
          primaryAction: 'SMART_PICKS',
          icon: '🍽️',
          variant: 'miq'
        });
        return;
      }
    }
    
    // RULE E: Meal Planner Exit Recovery
    // Trigger: User opened meal planner, exited without generating, meals_logged >= 3
    // Dismiss: 24 hours
    if (onboardingCompleted && aiNutritionGoalsSet && mealPlannerJustExited && effectiveMealsLogged >= 3) {
      if (!isPromptDismissed(userId, PROMPT_IDS.MEAL_PLANNER_EXIT)) {
        log('Showing: MEAL_PLANNER_EXIT (RULE E)');
        setCurrentPrompt({
          promptId: PROMPT_IDS.MEAL_PLANNER_EXIT,
          message: "Not planning ahead? MIQ can still suggest Smart Picks when eating out.",
          primaryLabel: "See Smart Picks",
          primaryAction: 'SMART_PICKS',
          icon: '🍽️',
          variant: 'miq'
        });
        return;
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // ═══════════════════════════════════════════════════════════════════════════
    // REAL-FOOD "LAST-MILE" SUGGESTIONS (Rules I & J) - DISABLED
    // These are now handled by the permanent SmartActionsCard "Quick Ideas" button
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Calculate if user has a meaningful calorie gap (100-500 kcal remaining)
    // Keeping for potential future use
    const hasCalorieGap = caloriesRemaining && dailyCalorieGoal && 
                          caloriesRemaining >= 100 && caloriesRemaining <= 500;
    
    // RULE I: Evening Snack Assist - DISABLED (now in SmartActionsCard)
    // RULE J: Missed Meal Recovery - DISABLED (now in SmartActionsCard)
    const expectedMealsByNow = isAfternoon() ? 2 : 1;
    const hasMissedMeals = totalMealsLogged < expectedMealsByNow;
    const hasLargeCalorieGap = caloriesRemaining && dailyCalorieGoal && caloriesRemaining >= 300;
    
    // STAGE 4: After commitment complete + rules A/B/C/D/E/I/J checked → View MIQ (if never visited)
    // Only show this if user has completed the commitment flow (onboarding done + goals set)
    if (onboardingCompleted && aiNutritionGoalsSet && totalMealsLogged >= 3 && !hasMIQBeenVisited) {
      if (!isPromptDismissed(userId, PROMPT_IDS.VIEW_MIQ)) {
        log('Showing: VIEW_MIQ');
        setCurrentPrompt({
          promptId: PROMPT_IDS.VIEW_MIQ,
          message: "Your MIQ is ready to explore — see what it learned.",
          primaryLabel: "View MIQ",
          primaryAction: 'VIEW_MIQ',
          icon: '🧠'
        });
        return;
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // SECONDARY PROMPTS: Only shown after commitment flow is complete
    // These are bonus/engagement prompts, not part of the core journey
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Only show secondary prompts if commitment is complete (onboarding done + AI goals set)
    if (!onboardingCompleted || !aiNutritionGoalsSet) {
      setCurrentPrompt(null);
      return;
    }
    
    // Secondary: Daily check-in (MIQ tier users)
    if (isMIQTier && !checkedInToday) {
      if (!isPromptDismissed(userId, PROMPT_IDS.DAILY_CHECKIN)) {
        log('Showing: DAILY_CHECKIN');
        setCurrentPrompt({
          promptId: PROMPT_IDS.DAILY_CHECKIN,
          message: "Quick check-in: confirm sleep & stress (10 seconds).",
          primaryLabel: "Daily Check-In",
          primaryAction: 'DAILY_CHECKIN',
          icon: '✨'
        });
        return;
      }
    }
    
    // Secondary: Premium features unused (barcode)
    if (isPremiumOrTrial && !hasUsedBarcodeScanner(userId) && totalMealsLogged >= 2) {
      if (!isPromptDismissed(userId, PROMPT_IDS.TRY_PREMIUM_FEATURE)) {
        log('Showing: TRY_PREMIUM_FEATURE');
        setCurrentPrompt({
          promptId: PROMPT_IDS.TRY_PREMIUM_FEATURE,
          message: "More accurate inputs improve MIQ confidence.",
          primaryLabel: "Try Barcode Scan",
          primaryAction: 'BARCODE_SCAN',
          icon: '📊'
        });
        return;
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // PHASE 1: GLOBAL "NEXT BEST ACTION" GUARANTEE
    // At no point should the user land on a screen with no clear primary action.
    // This fallback ensures ONE primary CTA is ALWAYS available.
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Determine the best fallback action based on user state
    const getFallbackAction = () => {
      // Priority 1: Profile not complete → Complete Profile
      if (!onboardingCompleted) {
        return {
          promptId: PROMPT_IDS.COMPLETE_PROFILE,
          message: "Complete your profile to unlock personalized insights.",
          primaryLabel: "🧍 Complete Profile",
          primaryAction: 'COMPLETE_PROFILE',
          icon: '📋',
          isFallback: true
        };
      }
      
      // Priority 2: No AI goals set → Activate SmartGoals (Intelligence style)
      if (!aiNutritionGoalsSet) {
        return {
          promptId: PROMPT_IDS.GOALS_TIMELINE_HOOK,
          message: "Fuel your goals with intelligence.",
          subMessage: "Set SmartGoals to activate personalized metabolic guidance powered by MIQ.",
          primaryLabel: "🎯 Activate SmartGoals",
          primaryAction: 'SET_GOALS_TIMELINE',
          icon: '🎯',
          variant: 'intelligence',
          isFallback: true
        };
      }
      
      // Priority 3: Haven't checked in today → Daily Check-In
      if (isMIQTier && !checkedInToday) {
        return {
          promptId: PROMPT_IDS.DAILY_CHECKIN,
          message: "Keep MIQ calibrated with a quick check-in.",
          primaryLabel: "☀️ Daily Check-In",
          primaryAction: 'DAILY_CHECKIN',
          icon: '☀️',
          isFallback: true
        };
      }
      
      // Priority 4: Haven't tried SmartMeals → Try SmartMeals
      if (!hasUsedMealPlanner(userId) && effectiveMealsLogged >= 3) {
        return {
          promptId: PROMPT_IDS.MEAL_PLANNER_NUDGE,
          message: "MIQ can suggest meals that fit your targets.",
          primaryLabel: "🍽️ Try SmartMeals",
          primaryAction: 'MEAL_PLANNER',
          icon: '🍽️',
          isFallback: true
        };
      }
      
      // Priority 5: Default → Log another meal (always valid)
      return {
        promptId: PROMPT_IDS.LOG_ANOTHER_MEAL,
        message: "Keep building MIQ confidence with another meal.",
        primaryLabel: "📸 Log Another Meal",
        primaryAction: 'TAKE_PHOTO',
        icon: '📸',
        isFallback: true
      };
    };
    
    // ALWAYS provide a fallback action - never leave user without a next step
    log('Showing: FALLBACK_ACTION (Next Best Action Guarantee)');
    setCurrentPrompt(getFallbackAction());
    
  }, [userId, userProfile, totalMealsLogged, allTimeMealsLogged, effectiveMealsLogged, hasGoal, hasTimeline, hasMealPattern,
      aiNutritionGoals, checkedInToday, isPremiumOrTrial, isMIQTier, isInModal, isTestMode,
      lastMealTimestamp, detectedRestaurantText, mealPlannerJustExited, caloriesRemaining, dailyCalorieGoal,
      todayCalories, advancedGoals, nutritionGoals]);
  
  // Dismiss handler
  const dismiss = useCallback(() => {
    if (currentPrompt && userId) {
      dismissPrompt(userId, currentPrompt.promptId);
      if (isTestMode) console.log(`[COACH] Dismissed: ${currentPrompt.promptId}`);
      setCurrentPrompt(null);
    }
  }, [currentPrompt, userId, isTestMode]);
  
  // Action clicked handler (for analytics)
  const onActionClick = useCallback(() => {
    if (currentPrompt && isTestMode) {
      console.log(`[COACH] Clicked: ${currentPrompt.promptId} -> ${currentPrompt.primaryAction}`);
    }
  }, [currentPrompt, isTestMode]);
  
  return {
    prompt: currentPrompt,
    dismiss,
    onActionClick,
    markMIQTabVisited: () => markMIQTabVisited(userId),
    markBarcodeUsed: () => {
      if (userId) localStorage.setItem(`barcode_used_${userId}`, 'true');
    },
    markMealPlannerUsed: () => markMealPlannerUsed(userId),
    markMealPlannerExitWithoutGenerate: () => markMealPlannerExitWithoutGenerate(userId),
    clearMealPlannerExitFlag: () => clearMealPlannerExitFlag(userId)
  };
};

export default useNextBestStep;
export { 
  PROMPT_IDS, 
  hasVisitedMIQTab, 
  markMIQTabVisited, 
  hasUsedMealPlanner, 
  markMealPlannerUsed,
  markMealPlannerExitWithoutGenerate,
  clearMealPlannerExitFlag,
  containsRestaurantKeyword,
  isLunchTime,
  isEveningTime,
  isAfternoon,
  EVENING_SNACK_SUGGESTIONS,
  MISSED_MEAL_SUGGESTIONS,
  getRandomSuggestion,
  hasShownJourneyStarted,
  markJourneyStartedShown
};
